import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { hcApi, hcError } from './api';
import { HcLayout } from './HcLayout';
import { coursePath, courseCta, CourseImage, LockedNote } from './HcCourseBits';

const TABS = [
  { id: 'all', label: 'All Courses', match: () => true },
  { id: 'in_progress', label: 'In Progress', match: (c) => ['in_progress', 'not_started', 'locked'].includes(c.status) },
  { id: 'completed', label: 'Completed', match: (c) => c.status === 'completed' },
];

export default function HcCoursesPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('all');

  useEffect(() => {
    hcApi.get('/courses').then(({ data }) => setCourses(data.courses)).catch((e) => setError(hcError(e)));
  }, []);

  const shown = (courses || []).filter(TABS.find((t) => t.id === tab).match);
  return (
    <HcLayout>
      <div className="max-w-6xl hc-fade-up" data-testid="hc-courses-page">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-[#363636]">My Courses</h1>
        <p className="text-gray-500 mt-2">Continue your learning journey. Select a course to view your progress.</p>
        <div className="flex flex-wrap gap-3 mt-6 mb-6">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`hc-courses-tab-${t.id}`}
              className={`px-6 py-2.5 rounded-xl font-semibold transition-colors ${tab === t.id ? 'bg-[#922A8C] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#922A8C]'}`}>
              {t.label} ({(courses || []).filter(t.match).length})
            </button>
          ))}
        </div>
        {!courses && !error && <Loader2 className="w-8 h-8 animate-spin text-[#922A8C]" />}
        {error && <p className="text-red-600">{error}</p>}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-100">
          {courses && shown.length === 0 && <p className="p-8 text-gray-500" data-testid="hc-courses-empty">No courses here yet.</p>}
          {shown.map((c) => (
            <div key={c.id} className="flex flex-col sm:flex-row sm:items-center gap-5 p-5" data-testid={`hc-course-row-${c.id}`}>
              <div className="w-full sm:w-44 shrink-0"><CourseImage course={c} className="w-full h-28 object-cover rounded-xl" /></div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-lg text-[#363636]">{c.title}</h3>
                <p className="text-sm text-gray-500 line-clamp-2 mt-1">{c.description}</p>
                <p className="text-xs text-gray-400 mt-1">{c.completed_lessons} of {c.total_lessons} lessons</p>
                {c.locked && <LockedNote message={c.locked_message} id={`row-${c.id}`} />}
              </div>
              <div className="text-center sm:w-24">
                <div className="text-3xl font-extrabold text-[#363636]">{c.progress}%</div>
                <div className="text-sm text-gray-500">Complete</div>
              </div>
              <button onClick={() => navigate(coursePath(c))} disabled={c.status === 'unavailable' || c.locked} data-testid={`hc-course-row-cta-${c.id}`}
                className="h-12 px-7 rounded-xl bg-[#922A8C] text-white font-semibold hover:bg-[#7a2275] transition-colors disabled:opacity-50">
                {c.status === 'unavailable' && !c.locked ? 'Unavailable' : courseCta(c)}
              </button>
            </div>
          ))}
        </div>
      </div>
    </HcLayout>
  );
}
